/**
 * @license
 * @copyright Copyright Motili Inc., 2025 All Rights Reserved
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DataGridFilter } from 'common/types/datagrid';
import { defaultAdvancedFilterId } from 'common/utils/constants';

interface Filter {
    [key: string]: DataGridFilter;
}

interface TableFilterState {
    activeFilters: Filter[];
    filterId: number;
    advancedFilterId: number;
}

const initialState: TableFilterState = {
    activeFilters: [],
    filterId: 101,
    advancedFilterId: defaultAdvancedFilterId,
};

export const createTableFilterSlice = (sliceName: string) => {
    const tableFilterSlice = createSlice({
        name: sliceName,
        initialState,
        reducers: {
            add: (state: TableFilterState, action: PayloadAction<Filter>) => {
                state.activeFilters.push(action.payload);
                state.filterId += 1;
            },
            replace: (
                state: TableFilterState,
                action: PayloadAction<Filter>
            ) => {
                state.activeFilters = [action.payload];
                state.filterId += 1;
            },
            setDefaultAdvancedFilters: (
                state: TableFilterState,
                action: PayloadAction<Filter[]>
            ) => {
                // Add default filters if filters not already present
                if (!state.activeFilters.length) {
                    state.activeFilters.push(...action.payload);
                }
            },
            addAdvanced: (
                state: TableFilterState,
                action: PayloadAction<{
                    filters: Filter[];
                    advancedFilterId: number;
                }>
            ) => {
                const { filters, advancedFilterId } = action.payload;
                state.activeFilters = [
                    ...state.activeFilters.filter(
                        item => Number(item.id) < 500
                    ),
                    ...filters,
                ];
                state.advancedFilterId = advancedFilterId;
            },
            updateAdvancedFilterId: (
                state: TableFilterState,
                action: PayloadAction<number>
            ) => {
                state.advancedFilterId = action.payload;
            },
            remove(
                state: TableFilterState,
                action: PayloadAction<string | number>
            ) {
                state.activeFilters = state.activeFilters.filter(
                    item => Number(item.id) !== Number(action.payload)
                );
            },
            removeAll(state: TableFilterState) {
                state.activeFilters = [];
            },
            incrementFilterId(state: TableFilterState) {
                state.filterId += 1;
            },
            incrementAdvancedFilterId(state: TableFilterState) {
                state.advancedFilterId += 1;
            },
        },
    });

    return {
        reducer: tableFilterSlice.reducer,
        actions: tableFilterSlice.actions,
    };
};
